import { useMemo } from "react";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { useLanguageContext } from "@/context/LanguageContext";
import api from "@/services/api";

/**
 * Hook to check if a revision has been requested for a specific section
 * and determine if it was requested by the current user or another user
 * 
 * @param section - The section to check (e.g., "compliance_document", "main_details", etc.)
 * @returns Object containing:
 *   - hasRevisionRequest: boolean - Whether a revision has been requested for this section
 *   - isCurrentUser: boolean - Whether the revision was requested by the current user
 *   - hoverMessage: string - The appropriate hover message to display
 */
export const useRevisionRequestCheck = (_section: string) => {
  const { activeProjectsProductInfo, selectedActiveProject, activeVendorTab } = useGlobalStateContext();
  const { languageText } = useLanguageContext();
  
  const result = useMemo(() => {
    // Get current user's userid from token
    const userData = api.getUserData();
    const currentUserId = userData?.userid || null;
    
    // Get the active vendor index (default to 0 if not set)
    const vendorIndex = activeVendorTab ?? 0;
    
    // Get revisionRequestedBy from vendorDetails array - check both activeProjectsProductInfo and selectedActiveProject
    // The revisionRequestedBy is nested inside vendorDetails[activeVendorTab]
    const vendorDetails = activeProjectsProductInfo?.vendorDetails || selectedActiveProject?.vendorDetails;
    const currentVendor = Array.isArray(vendorDetails) && vendorDetails.length > vendorIndex ? vendorDetails[vendorIndex] : null;
    const revisionRequestedBy = currentVendor?.revisionRequestedBy;
    
    // Default values
    let hasRevisionRequest = false;
    let isCurrentUser = false;
    let hoverMessage = "";
    
    // New requirement: don't check for section; any revision request disables approval.
    // Normalize `revisionRequestedBy` into an array as backend shape may vary.
    const revisionRequestedByArr: any[] = (() => {
      if (!revisionRequestedBy) return [];
      if (Array.isArray(revisionRequestedBy)) return revisionRequestedBy;
      if (Array.isArray((revisionRequestedBy as any)?.data)) return (revisionRequestedBy as any).data;
      if (typeof revisionRequestedBy === "string") {
        try {
          const parsed = JSON.parse(revisionRequestedBy);
          if (Array.isArray(parsed)) return parsed;
          if (Array.isArray((parsed as any)?.data)) return (parsed as any).data;
          if (parsed && typeof parsed === "object") return Object.values(parsed as any);
        } catch {
          return [];
        }
      }
      if (revisionRequestedBy && typeof revisionRequestedBy === "object") {
        return Object.values(revisionRequestedBy as any);
      }
      return [];
    })();

    if (revisionRequestedByArr.length > 0) {
      hasRevisionRequest = true;

      const getRequestedByUserId = (item: any) =>
        item?.portalUserId ??
        null;

      isCurrentUser = Boolean(
        currentUserId &&
          revisionRequestedByArr.some((item: any) => String(getRequestedByUserId(item) ?? "") === String(currentUserId))
      );

      hoverMessage = isCurrentUser
        ? (languageText?.MESSAGES?.VALIDATION_MESSAGES?.RECIPE_AND_RAW_MATERIALS?.REVIEW_COMPLETE_SAME_USER_TOOLTIP ||
            "Approval is disabled because you have requested the vendor for revisions.")
        : (languageText?.MESSAGES?.VALIDATION_MESSAGES?.RECIPE_AND_RAW_MATERIALS?.REVIEW_COMPLETE_OTHER_USER_TOOLTIP ||
            "Approval is disabled because another reviewer has requested revisions.");
    }
    
    return {
      hasRevisionRequest,
      isCurrentUser,
      hoverMessage
    };
  }, [activeProjectsProductInfo, selectedActiveProject, activeVendorTab, languageText]);
  
  return result;
};

export default useRevisionRequestCheck;

